import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { FiAlertTriangle, FiClock, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { HiOutlineLink, HiOutlineShieldCheck } from 'react-icons/hi';
import { MdClose, MdOutlineEdit, MdOutlineHourglassTop } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Search from 'components/Common/Search';
import { useDeleteA2pCampaign, useGetA2pBrands, useGetA2pCampaigns } from '@/apis/business/setting/phoneNumbers/hook';
import { A2pBrand, A2pCampaign } from '@/apis/business/setting/phoneNumbers/types';
import { confirmDialog, titleCase } from 'utils/helperFunctions';
import RegisterBrandDrawer from './RegisterBrandDrawer';
import BrandVerificationModal from './BrandVerificationModal';
import CampaignFormDrawer from './CampaignFormDrawer';
import LinkNumbersDrawer from './LinkNumbersDrawer';
import LinkedNumbersDrawer from './LinkedNumbersDrawer';
import { A2P_POLL_INTERVAL_MS, BRAND_REJECTED_FALLBACK } from './a2pConstants';
import { campaignTypeLabel } from './campaignFormShared';
import PromptBuilderEmptyStateIcon from '@/assets/icons/PromptBuilderEmptyStateIcon';

const CARD_BORDER = '#EDEEF0';

const pillSx = { textTransform: 'none', fontWeight: 500, borderRadius: '999px' } as const;

const detailStr = (details: Record<string, unknown> | null | undefined, ...keys: string[]) => {
  if (!details) return '';
  for (const key of keys) {
    const value = details[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
};

const campaignStatusPresentation = (status?: string | null) => {
  switch (status) {
    case 'approved':
      return { label: 'Approved', bg: '#D1FAE5', color: '#047857' };
    case 'in_review':
      return { label: 'In Review', bg: '#FEF3C7', color: '#B45309' };
    case 'rejected':
      return { label: 'Rejected', bg: '#FEE2E2', color: '#DC2626' };
    default:
      return {
        label: titleCase((status || 'pending').replace(/_/g, ' ')),
        bg: '#F3F4F6',
        color: '#6B7280'
      };
  }
};

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toISOString().slice(0, 19).replace('T', ' ');
};

function StatusCard({ children }: { children: ReactNode }) {
  return (
    <Box
      bgcolor='background.paper'
      border={`1px solid ${CARD_BORDER}`}
      borderRadius='8px'
      px={{ xs: 2, md: 4 }}
      py={3}
      display='flex'
      flexDirection='column'
      alignItems='center'
      textAlign='center'
    >
      {children}
    </Box>
  );
}

function StatusIcon({ bg, children }: { bg: string; children: ReactNode }) {
  return (
    <Box
      width={72}
      height={72}
      borderRadius='50%'
      bgcolor={bg}
      display='flex'
      alignItems='center'
      justifyContent='center'
      mb={2}
    >
      {children}
    </Box>
  );
}

type Props = {
  /** When true, omit the standalone page title (card chrome lives in Telephony Add-Ons). */
  embedded?: boolean;
};

export default function A2pCampaignTab({ embedded = false }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerBrand, setDrawerBrand] = useState<A2pBrand | null>(null);
  const [submittedBrand, setSubmittedBrand] = useState<A2pBrand | null>(null);
  const [campaignDrawerOpen, setCampaignDrawerOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<A2pCampaign | null>(null);
  const [linkCampaign, setLinkCampaign] = useState<A2pCampaign | null>(null);
  const [linkedCampaign, setLinkedCampaign] = useState<A2pCampaign | null>(null);
  const [searchText, setSearchText] = useState<string | null>('');

  const { data: brandsData, isLoading: brandLoading } = useGetA2pBrands();
  const brand = brandsData?.data?.[0] ?? null;
  const brandApproved = brand?.status === 'approved';

  const { data: campaignsData, isLoading: campaignsLoading } = useGetA2pCampaigns(
    { per_page: 100 },
    {
      enabled: brandApproved,
      refetchInterval: (query) =>
        query.state.data?.data?.some((row) => (row.numbers_pending_count ?? 0) > 0) ? A2P_POLL_INTERVAL_MS : false
    }
  );
  const campaigns = useMemo(() => {
    const rows = campaignsData?.data ?? [];
    const query = (searchText || '').trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => {
      const name = (row.name || row.campaign_type || '').toLowerCase();
      const vertical = (row.vertical || '').replace(/_/g, ' ').toLowerCase();
      return name.includes(query) || vertical.includes(query);
    });
  }, [campaignsData, searchText]);
  const deleteCampaign = useDeleteA2pCampaign();

  const openBrandDrawer = (existing: A2pBrand | null) => {
    setDrawerBrand(existing);
    setDrawerOpen(true);
  };

  const handleBrandSubmitted = (result: A2pBrand) => {
    setSubmittedBrand(result);
  };

  const closeVerification = () => {
    setSubmittedBrand(null);
    setDrawerOpen(false);
  };

  const handleDeleteCampaign = useCallback(
    async (campaign: A2pCampaign) => {
      const linked = campaign.numbers_linked_count ?? 0;
      const result = await confirmDialog({
        title: 'Delete campaign?',
        text: linked
          ? `“${campaign.name || campaignTypeLabel(campaign.campaign_type) || campaign.campaign_id}” will be deactivated and its ${linked} linked number(s) will stop sending SMS.`
          : `Deactivate “${campaign.name || campaignTypeLabel(campaign.campaign_type) || campaign.campaign_id}”?`,
        icon: 'warning',
        iconColor: '#DA0808',
        confirmButtonColor: '#DA0808',
        confirmButtonText: 'Delete'
      });
      if (!result.isConfirmed) return;
      deleteCampaign.mutate(campaign.id);
    },
    [deleteCampaign]
  );

  const columns = useMemo<ColumnDef<A2pCampaign>[]>(
    () => [
      {
        header: 'CAMPAIGN',
        accessorKey: 'name',
        cell: ({ row }) => {
          const name = row.original.name || campaignTypeLabel(row.original.campaign_type) || '—';
          const vertical = row.original.vertical ? titleCase(row.original.vertical.replace(/_/g, ' ')) : '';
          return (
            <Box>
              <Typography fontSize={14} color='text.primary' lineHeight={1.35}>
                {name}
              </Typography>
              {vertical && (
                <Typography fontSize={12} color='text.secondary' lineHeight={1.3}>
                  {vertical}
                </Typography>
              )}
            </Box>
          );
        }
      },
      {
        header: 'CAMPAIGN TYPE',
        accessorKey: 'campaign_type',
        cell: ({ row }) => (
          <Typography fontSize={14} color='text.primary'>
            {campaignTypeLabel(row.original.campaign_type) || '—'}
          </Typography>
        )
      },
      {
        header: 'CREATED ON',
        accessorKey: 'created_at',
        cell: ({ row }) => (
          <Typography fontSize={14} color='text.primary'>
            {formatDateTime(row.original.created_at)}
          </Typography>
        )
      },
      {
        header: 'NUMBER LINKED',
        accessorKey: 'numbers_linked_count',
        cell: ({ row }) => {
          const linked = (row.original.numbers_linked_count ?? 0) > 0;
          const pending = row.original.numbers_pending_count ?? 0;
          return (
            <Stack direction='row' spacing={0.75} alignItems='center'>
              <Typography
                fontSize={14}
                color={linked ? '#2563EB' : 'text.primary'}
                sx={linked ? { textDecoration: 'underline', cursor: 'pointer' } : undefined}
                onClick={linked ? () => setLinkedCampaign(row.original) : undefined}
              >
                {linked ? 'Yes' : 'No'}
              </Typography>
              {pending > 0 && (
                <Tooltip title='Number linking in progress' arrow>
                  <Box component='span' display='inline-flex' color='#F59E0B'>
                    <FiClock size={16} />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          );
        }
      },
      {
        header: 'STATUS',
        accessorKey: 'status',
        cell: ({ row }) => {
          const presentation = campaignStatusPresentation(row.original.status);
          return (
            <Chip
              label={presentation.label}
              size='small'
              sx={{
                bgcolor: presentation.bg,
                color: presentation.color,
                fontWeight: 500,
                fontSize: 12,
                borderRadius: '999px',
                height: 24
              }}
            />
          );
        }
      },
      {
        header: 'ACTIONS',
        id: 'actions',
        cell: ({ row }) => {
          const canManageNumbers = row.original.status === 'approved' || (row.original.numbers_linked_count ?? 0) > 0;
          const needsNumberLink = row.original.status === 'approved' && (row.original.numbers_linked_count ?? 0) === 0;
          return (
            <Box display='flex' alignItems='center'>
              {needsNumberLink && (
                <Tooltip
                  title='Your campaign has been approved, but no number has been linked yet. Please link a number within 60 days to avoid a $250 non-use fee.'
                  arrow
                  placement='top'
                >
                  <IconButton aria-label='Number link required' sx={{ color: '#D97706' }}>
                    <FiAlertTriangle size={18} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip
                title={canManageNumbers ? 'Link or unlink numbers' : 'Link numbers after the campaign is approved'}
              >
                <span>
                  <IconButton
                    aria-label='Link numbers'
                    disabled={!canManageNumbers}
                    onClick={() => setLinkCampaign(row.original)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <HiOutlineLink />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Edit campaign'>
                <IconButton
                  aria-label='Edit'
                  onClick={() => {
                    setEditingCampaign(row.original);
                    setCampaignDrawerOpen(true);
                  }}
                >
                  <MdOutlineEdit />
                </IconButton>
              </Tooltip>
              <Tooltip title='Delete campaign'>
                <IconButton aria-label='Delete' onClick={() => handleDeleteCampaign(row.original)} color='error'>
                  <RiDeleteBin6Line style={{ color: 'red' }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        }
      }
    ],
    [handleDeleteCampaign]
  );

  const brandTooltip = brand
    ? [
        `Brand: ${brand.display_name}`,
        `EIN: ${brand.ein || '—'}`,
        `Support Email: ${detailStr(brand.details, 'email') || '—'}`,
        `Phone Number: ${detailStr(brand.details, 'phone') || '—'}`
      ].join('\n')
    : '';

  const renderContent = () => {
    if (brandLoading) {
      return (
        <StatusCard>
          <Typography fontSize={13} color='text.secondary'>
            Loading brand…
          </Typography>
        </StatusCard>
      );
    }

    if (!brand) {
      return (
        <StatusCard>
          <Box sx={{ '& svg': { width: 160, height: 'auto' } }}>
            <PromptBuilderEmptyStateIcon />
          </Box>
          <Typography fontSize={13} color='text.secondary' maxWidth={720} lineHeight={1.6} mt={1} mb={2}>
            You haven&apos;t registered your brand yet. Only registered brand can be used to create campaigns. Click on
            &quot;Register Brand&quot; to proceed ahead.
          </Typography>
          <Button
            variant='contained'
            color='primary'
            startIcon={<FiPlus size={16} />}
            onClick={() => openBrandDrawer(null)}
            sx={{ ...pillSx, minWidth: 230, px: 3 }}
          >
            Register Brand
          </Button>
        </StatusCard>
      );
    }

    if (brand.status === 'rejected') {
      return (
        <StatusCard>
          <StatusIcon bg='#FDECEC'>
            <MdClose size={34} color='#DC2626' />
          </StatusIcon>
          <Typography fontSize={18} fontWeight={600} color='text.primary' mb={2}>
            Your Brand &quot;{brand.display_name}&quot; was rejected
          </Typography>
          <Box bgcolor='#FDECEC' borderRadius='4px' px={2} py={1.25} mb={2} width='100%' textAlign='left'>
            <Typography fontSize={13} color='#DC2626' lineHeight={1.6}>
              {brand.rejection_reason || BRAND_REJECTED_FALLBACK}
            </Typography>
          </Box>
          <Typography fontSize={13} color='text.secondary' lineHeight={1.6} mb={2}>
            Unfortunately, your brand submission did not meet the carrier&apos;s verification requirements. Please
            review the reason, make necessary corrections, and resubmit your brand for approval.
          </Typography>
          <Button
            variant='contained'
            color='primary'
            startIcon={<FiRefreshCw size={16} />}
            onClick={() => openBrandDrawer(brand)}
            sx={{ ...pillSx, minWidth: 226, px: 3 }}
          >
            Resubmit Brand
          </Button>
        </StatusCard>
      );
    }

    if (!brandApproved) {
      return (
        <StatusCard>
          <StatusIcon bg='#FEF6E4'>
            <MdOutlineHourglassTop size={32} color='#D97706' />
          </StatusIcon>
          <Typography fontSize={18} fontWeight={600} color='text.primary' mb={1.5}>
            Your Brand “{brand.display_name}” is in review
          </Typography>
          <Typography fontSize={13} color='text.secondary' maxWidth={560} lineHeight={1.6}>
            This usually takes 1–3 business days. We&apos;ll notify you once the carrier completes verification. You can
            create campaigns once your brand is approved.
          </Typography>
        </StatusCard>
      );
    }

    return (
      <DataTable
        data={campaigns}
        columns={columns}
        isLoading={campaignsLoading}
        isHeaderSticky={false}
        enableColumnResizing={false}
        containerStyles={{ borderBottom: '1px solid #ECECEC' }}
      />
    );
  };

  const showHeader = !embedded || brandApproved;

  return (
    <Box>
      {showHeader && (
        <Box
          display='flex'
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          gap={2}
          mb={3}
        >
          <Box>
            <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
              {!embedded && (
                <Typography variant='h6' fontWeight={500}>
                  A2P Campaign
                </Typography>
              )}
              {brandApproved && brand && (
                <Tooltip title={<Box whiteSpace='pre-line'>{brandTooltip}</Box>} arrow placement='bottom-start'>
                  <Chip
                    icon={<HiOutlineShieldCheck size={16} />}
                    label={brand.display_name}
                    size='small'
                    sx={{
                      bgcolor: '#D1FAE5',
                      color: '#047857',
                      fontWeight: 600,
                      borderRadius: '999px',
                      height: 28,
                      '& .MuiChip-icon': { color: '#047857' }
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            {!embedded && (
              <Typography fontSize={13} color='text.secondary' mt={0.5}>
                A2P Campaign registration is mandatory in the USA before sending SMS messages.
              </Typography>
            )}
          </Box>

          {brandApproved && (
            <Stack direction='row' spacing={1.5} alignItems='center' flexWrap='wrap'>
              {!embedded && (
                <Search
                  placeholder='Search campaigns'
                  size='small'
                  debounceTime={300}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  sx={{
                    minWidth: { xs: '100%', sm: 200 },
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': { borderRadius: '6px', minHeight: 36 }
                  }}
                />
              )}
              <Button
                variant='contained'
                color='primary'
                startIcon={<FiPlus size={16} />}
                onClick={() => {
                  setEditingCampaign(null);
                  setCampaignDrawerOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  borderRadius: '999px',
                  px: 3,
                  py: 0.75,
                  minHeight: 36,
                  height: 36
                }}
              >
                Register New Campaign
              </Button>
            </Stack>
          )}
        </Box>
      )}

      {renderContent()}

      <RegisterBrandDrawer
        open={drawerOpen}
        brand={drawerBrand}
        onClose={() => setDrawerOpen(false)}
        onSubmitted={handleBrandSubmitted}
      />
      <BrandVerificationModal open={Boolean(submittedBrand)} brand={submittedBrand} onClose={closeVerification} />
      <CampaignFormDrawer
        open={campaignDrawerOpen}
        campaign={editingCampaign}
        brand={brand}
        onClose={() => {
          setCampaignDrawerOpen(false);
          setEditingCampaign(null);
        }}
      />
      <LinkNumbersDrawer open={Boolean(linkCampaign)} campaign={linkCampaign} onClose={() => setLinkCampaign(null)} />
      <LinkedNumbersDrawer
        open={Boolean(linkedCampaign)}
        campaign={linkedCampaign}
        onClose={() => setLinkedCampaign(null)}
      />
    </Box>
  );
}
